import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { trpc } from '@/lib/trpc';

const VISITOR_KEY = 'wsl_visitor_id';

/**
 * Stable anonymous visitor id, persisted in localStorage. NOT PII — just a
 * random opaque token so we can count unique visitors without a login. Created
 * once per browser; reused across sessions.
 */
function getVisitorId(): string | undefined {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // localStorage blocked (private mode / SSR) — track without a visitor id.
    return undefined;
  }
}

/**
 * Fires a first-party page-view event on every route change. Used to populate
 * our own `page_views` table so the admin dashboard shows real Landing/Pricing
 * traffic independent of PostHog.
 *
 * Fire-and-forget: a failed call never affects the page. Deduped per-path
 * within a short window so React strict-mode double-renders and rapid
 * redirect chains don't double-count a single visit.
 */
export function usePageViewTracking(): void {
  const [location] = useLocation();
  const lastTracked = useRef<{ path: string; at: number } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname || location || '/';

    // Dedup: same path within 1s = same visit (strict-mode / redirect noise).
    const now = Date.now();
    const last = lastTracked.current;
    if (last && last.path === path && now - last.at < 1000) return;
    lastTracked.current = { path, at: now };

    // Campaign attribution: when the URL carries UTM params (e.g. the Snapchat
    // link ?utm_source=snapchat&utm_campaign=snap20), tag this view's referrer as
    // `utm:{source}:{campaign}` so the admin can count campaign traffic — the
    // UTM is a more reliable source than an in-app-browser document.referrer.
    // Otherwise fall back to the real referrer. Zero new columns.
    const realReferrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined;
    let referrer = realReferrer;
    try {
      const params = new URLSearchParams(window.location.search);
      const src = params.get('utm_source');
      const camp = params.get('utm_campaign');
      if (src || camp) {
        referrer = `utm:${(src || 'unknown').toLowerCase().slice(0, 40)}:${(camp || '').toLowerCase().slice(0, 40)}`;
      }
    } catch { /* no-op — best-effort */ }

    // Fire-and-forget — tracking must never throw into the render path.
    trpc.analytics
      .trackPageView({ path, visitorId: getVisitorId(), referrer })
      .catch(() => {
        /* silent — analytics is best-effort */
      });
  }, [location]);
}
